import axios from 'axios';
import { NOTIFICATION_ACTION_TYPES, baseURL } from './types'; // Ensure types.js exists

// ==================== AUTH & UTILITY FUNCTIONS (Adopted from videoSettingsAction.js) ====================

// Utility: Get token from localStorage
const getAuthToken = () => {
    try {
        const loginData = JSON.parse(localStorage.getItem('loginData') || '{}');
        const directToken = localStorage.getItem('token');
        return loginData.token || directToken || null;
    } catch (error) {
        console.error('Error retrieving auth token:', error);
        return null;
    }
};

// Get auth headers configuration
const getAuthHeaders = () => {
    const token = getAuthToken();
    if (!token) {
        throw new Error('No authentication token found');
    }

    return {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };
};

// Utility to ensure URL is correctly formatted
const normalizeUrl = (base, path) => {
    const normalizedBase = base.endsWith('/') ? base : base + '/';
    const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
    return normalizedBase + normalizedPath;
};

// Enhanced error handler (from videoSettingsAction.js)
const handleApiError = (error, dispatch, failType, shouldReject = true) => {
    console.error('API Error:', error);

    let errorMessage = 'An unexpected error occurred';

    if (error.response) {
        // Handle common API errors (404, 401, 500)
        errorMessage = error.response.data?.message || 
                       error.response.data?.error || 
                       `Server error: ${error.response.status}`;
    } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
    } else {
        errorMessage = error.message;
    }

    dispatch({
        type: failType,
        payload: errorMessage,
    });

    if (shouldReject) {
        return Promise.reject(new Error(errorMessage));
    }
    // If not rejecting, return a placeholder Error object for type consistency
    return new Error(errorMessage); 
};

// Generic API action creator for POST/PATCH/DELETE (from videoSettingsAction.js)
const createApiAction = (types, path, method = 'post', data = null) => async (dispatch) => {
    try {
        dispatch({ type: types.REQUEST });
        const url = normalizeUrl(baseURL, path); 
        const config = getAuthHeaders();
        let response;

        switch (method.toLowerCase()) {
            case 'post':
                response = await axios.post(url, data, config);
                break;
            case 'patch':
                response = await axios.patch(url, data, config);
                break;
            case 'delete':
                response = await axios.delete(url, { ...config, data });
                break;
            default:
                throw new Error('Unsupported method for createApiAction');
        }

        const payload = response.data.success === 1 ? (response.data.data || response.data) : response.data;

        dispatch({
            type: types.SUCCESS,
            payload,
        });
        return Promise.resolve(payload);

    } catch (error) {
        return handleApiError(error, dispatch, types.FAIL, true);
    }
};

// Generic GET action with graceful error handling (based on videoSettingsAction.js)
// This will resolve to [] on failure, preventing Unhandled Rejection.
const createGetAllAction = (types, path, idKey = 'hid') => async (dispatch) => {
    try {
        dispatch({ type: types.REQUEST });
        const url = normalizeUrl(baseURL, path); 
        const config = getAuthHeaders();

        // Retrieve hospitalId from localStorage for the API request payload
        const hospitalId = JSON.parse(localStorage.getItem('loginData') || '{}').hospitalID || '0';

        const payloadData = { [idKey]: hospitalId };

        const { data } = await axios.post( 
            url,
            payloadData, 
            config
        );

        const settings = data.success === 1 ? (data.data || []) : [];

        dispatch({
            type: types.SUCCESS,
            payload: settings,
        });
        return Promise.resolve(settings);

    } catch (error) {
        // Graceful failure: Sets error but resolves to [] to prevent component crash
        handleApiError(error, dispatch, types.FAIL, false); 
        
        // Ensure the SUCCESS state is still dispatched with an empty array
        dispatch({
            type: types.SUCCESS,
            payload: [],
        });
        return Promise.resolve([]);
    }
};

// ==================== EMAIL SETTINGS ====================

export const createEmailSetting = (emailData) =>
    createApiAction(
        {
            REQUEST: NOTIFICATION_ACTION_TYPES.CREATE_EMAIL_SETTING_REQUEST,
            SUCCESS: NOTIFICATION_ACTION_TYPES.CREATE_EMAIL_SETTING_SUCCESS,
            FAIL: NOTIFICATION_ACTION_TYPES.CREATE_EMAIL_SETTING_FAIL
        },
        'api/appointementEmailSetting/create',
        'post',
        emailData
    );

export const getAllEmailSettings = (hid) => // hid parameter is for consistency but ignored internally
    createGetAllAction(
        {
            REQUEST: NOTIFICATION_ACTION_TYPES.GET_EMAIL_SETTINGS_REQUEST,
            SUCCESS: NOTIFICATION_ACTION_TYPES.GET_EMAIL_SETTINGS_SUCCESS,
            FAIL: NOTIFICATION_ACTION_TYPES.GET_EMAIL_SETTINGS_FAIL
        },
        '/api/appointementEmailSetting/getall',
        'hid'
    );

export const updateEmailSetting = (emailData) =>
    createApiAction(
        {
            REQUEST: NOTIFICATION_ACTION_TYPES.UPDATE_EMAIL_SETTING_REQUEST,
            SUCCESS: NOTIFICATION_ACTION_TYPES.UPDATE_EMAIL_SETTING_SUCCESS,
            FAIL: NOTIFICATION_ACTION_TYPES.UPDATE_EMAIL_SETTING_FAIL
        },
        'api/appointementEmailSetting/update',
        'patch',
        emailData
    );

// export const deleteEmailSetting = (id) =>
//     createApiAction(
//         {
//             REQUEST: NOTIFICATION_ACTION_TYPES.DELETE_EMAIL_SETTING_REQUEST,
//             SUCCESS: NOTIFICATION_ACTION_TYPES.DELETE_EMAIL_SETTING_SUCCESS,
//             FAIL: NOTIFICATION_ACTION_TYPES.DELETE_EMAIL_SETTING_FAIL
//         },
//         'api/appointmentEmailSetting/delete',
//         'delete',
//         { id }
//     );
// ==================== TWILIO SMS SETTINGS (API 1) ====================
// actions/notificationActions.js mein ise replace karein
export const deleteEmailSetting = (id, hid) =>
    createApiAction(
        {
            REQUEST: NOTIFICATION_ACTION_TYPES.DELETE_EMAIL_SETTING_REQUEST,
            SUCCESS: NOTIFICATION_ACTION_TYPES.DELETE_EMAIL_SETTING_SUCCESS,
            FAIL: NOTIFICATION_ACTION_TYPES.DELETE_EMAIL_SETTING_FAIL
        },
        'api/appointementEmailSetting/delete', 
        'delete',
        { "id": Number(id), "hid": String(hid) } // Dono parameters yaha pass karein
    );
export const createTwilioSmsSetting = (data) =>
    createApiAction(
        {
            REQUEST: NOTIFICATION_ACTION_TYPES.CREATE_TWILIO_SMS_SETTING_REQUEST,
            SUCCESS: NOTIFICATION_ACTION_TYPES.CREATE_TWILIO_SMS_SETTING_SUCCESS,
            FAIL: NOTIFICATION_ACTION_TYPES.CREATE_TWILIO_SMS_SETTING_FAIL
        },
        'api/appointementSmsSetting/create1',
        'post',
        data
    );

export const getAllTwilioSmsSettings = (hid) =>
    createGetAllAction(
        {
            REQUEST: NOTIFICATION_ACTION_TYPES.GET_TWILIO_SMS_SETTINGS_REQUEST,
            SUCCESS: NOTIFICATION_ACTION_TYPES.GET_TWILIO_SMS_SETTINGS_SUCCESS,
            FAIL: NOTIFICATION_ACTION_TYPES.GET_TWILIO_SMS_SETTINGS_FAIL
        },
        '/api/appointementSmsSetting/getall1',
        'hid'
    );

export const updateTwilioSmsSetting = (data) =>
    createApiAction(
        {
            REQUEST: NOTIFICATION_ACTION_TYPES.UPDATE_TWILIO_SMS_SETTING_REQUEST,
            SUCCESS: NOTIFICATION_ACTION_TYPES.UPDATE_TWILIO_SMS_SETTING_SUCCESS,
            FAIL: NOTIFICATION_ACTION_TYPES.UPDATE_TWILIO_SMS_SETTING_FAIL
        },
        'api/appointementSmsSetting/update1',
        'patch',
        data
    );

export const deleteTwilioSmsSetting = (id) =>
    createApiAction(
        {
            REQUEST: NOTIFICATION_ACTION_TYPES.DELETE_TWILIO_SMS_SETTING_REQUEST,
            SUCCESS: NOTIFICATION_ACTION_TYPES.DELETE_TWILIO_SMS_SETTING_SUCCESS,
            FAIL: NOTIFICATION_ACTION_TYPES.DELETE_TWILIO_SMS_SETTING_FAIL
        },
        'api/appointementSmsSetting/delete1',
        'delete',
        { id }
    );
// ==================== MSG CLUB SMS SETTINGS ====================

export const createMsgClubSmsSetting = (msgClubData) =>
    createApiAction(
        {
            REQUEST: NOTIFICATION_ACTION_TYPES.CREATE_MSGCLUB_SMS_SETTING_REQUEST,
            SUCCESS: NOTIFICATION_ACTION_TYPES.CREATE_MSGCLUB_SMS_SETTING_SUCCESS,
            FAIL: NOTIFICATION_ACTION_TYPES.CREATE_MSGCLUB_SMS_SETTING_FAIL
        },
        'api/appointementSmsSetting/create2',
        'post',
        msgClubData
    );

export const getAllMsgClubSmsSettings = (hid) => // hid parameter is for consistency but ignored internally
    createGetAllAction(
        {
            REQUEST: NOTIFICATION_ACTION_TYPES.GET_MSGCLUB_SMS_SETTINGS_REQUEST,
            SUCCESS: NOTIFICATION_ACTION_TYPES.GET_MSGCLUB_SMS_SETTINGS_SUCCESS,
            FAIL: NOTIFICATION_ACTION_TYPES.GET_MSGCLUB_SMS_SETTINGS_FAIL
        },
        '/api/appointementSmsSetting/getall2',
        'hid'
    );

export const updateMsgClubSmsSetting = (msgClubData) =>
    createApiAction(
        {
            REQUEST: NOTIFICATION_ACTION_TYPES.UPDATE_MSGCLUB_SMS_SETTING_REQUEST,
            SUCCESS: NOTIFICATION_ACTION_TYPES.UPDATE_MSGCLUB_SMS_SETTING_SUCCESS,
            FAIL: NOTIFICATION_ACTION_TYPES.UPDATE_MSGCLUB_SMS_SETTING_FAIL
        },
        'api/appointementSmsSetting/update2',
        'patch',
        msgClubData
    );

export const deleteMsgClubSmsSetting = (id) =>
    createApiAction(
        {
            REQUEST: NOTIFICATION_ACTION_TYPES.DELETE_MSGCLUB_SMS_SETTING_REQUEST,
            SUCCESS: NOTIFICATION_ACTION_TYPES.DELETE_MSGCLUB_SMS_SETTING_SUCCESS,
            FAIL: NOTIFICATION_ACTION_TYPES.DELETE_MSGCLUB_SMS_SETTING_FAIL
        },
        'api/appointementSmsSetting/delete2',
        'delete',
        { id }
    );

// ==================== NOTIFICATION SETTINGS ====================

export const createNotificationSetting = (notificationData) =>
    createApiAction(
        {
            REQUEST: NOTIFICATION_ACTION_TYPES.CREATE_NOTIFICATION_SETTING_REQUEST,
            SUCCESS: NOTIFICATION_ACTION_TYPES.CREATE_NOTIFICATION_SETTING_SUCCESS,
            FAIL: NOTIFICATION_ACTION_TYPES.CREATE_NOTIFICATION_SETTING_FAIL
        },
        'api/appointementNotificationSetting/create',
        'post',
        notificationData
    );

export const getAllNotificationSettings = (hospitalID) => // hospitalID parameter is for consistency but ignored internally
    createGetAllAction(
        {
            REQUEST: NOTIFICATION_ACTION_TYPES.GET_NOTIFICATION_SETTINGS_REQUEST,
            SUCCESS: NOTIFICATION_ACTION_TYPES.GET_NOTIFICATION_SETTINGS_SUCCESS,
            FAIL: NOTIFICATION_ACTION_TYPES.GET_NOTIFICATION_SETTINGS_FAIL
        },
        '/api/appointementNotificationSetting/getall',
        'hospitalID' // Use hospitalID as the key for this endpoint
    );

export const updateNotificationSetting = (notificationData) =>
    createApiAction(
        {
            REQUEST: NOTIFICATION_ACTION_TYPES.UPDATE_NOTIFICATION_SETTING_REQUEST,
            SUCCESS: NOTIFICATION_ACTION_TYPES.UPDATE_NOTIFICATION_SETTING_SUCCESS,
            FAIL: NOTIFICATION_ACTION_TYPES.UPDATE_NOTIFICATION_SETTING_FAIL
        },
        'api/appointementNotificationSetting/update',
        'patch',
        notificationData
    );

export const deleteNotificationSetting = (id,hid) =>
    createApiAction(
        {
            REQUEST: NOTIFICATION_ACTION_TYPES.DELETE_NOTIFICATION_SETTING_REQUEST,
            SUCCESS: NOTIFICATION_ACTION_TYPES.DELETE_NOTIFICATION_SETTING_SUCCESS,
            FAIL: NOTIFICATION_ACTION_TYPES.DELETE_NOTIFICATION_SETTING_FAIL
        },
        'api/appointementNotificationSetting/delete',
        'delete',
        { "id":id, "hospitalID":hid }
    );

// Reset Notification State
export const resetNotificationState = () => (dispatch) => {
    dispatch({ type: NOTIFICATION_ACTION_TYPES.RESET_NOTIFICATION_STATE });
};